import { useEffect, useState } from "react";
import { Plus, Trash2, Send, Copy, Loader2, X, Video, PhoneCall } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import AdminHeader from "@/components/AdminHeader";
import WebRTCCall from "@/components/WebRTCCall";
import CallFiles from "@/components/CallFiles";

const inputCls = "w-full h-11 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]";
const emptyForm = { title: "", client_id: "", client_name: "", contact_email: "", contact_name: "", event_id: "", date: "", time: "", duration: 30 };

const TODAY = new Date().toISOString().slice(0, 10);
const upcomingOnly = (list) => (list || [])
  .filter((e) => e.id && (!e.event_date || e.event_date >= TODAY))
  .sort((a, b) => (a.event_date || "9999").localeCompare(b.event_date || "9999"));

export default function AdminCalls() {
  const [list, setList] = useState(null);
  const [clients, setClients] = useState([]);
  const [events, setEvents] = useState([]);
  const [me, setMe] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendFor, setSendFor] = useState(null);
  const [sendTo, setSendTo] = useState("");
  const [sending, setSending] = useState(false);
  const [joining, setJoining] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/calls");
      setList(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => {
    load();
    api.get("/clients").then(({ data }) => setClients(data || [])).catch(() => {});
    api.get("/events").then(({ data }) => setEvents(upcomingOnly(data))).catch(() => {});
    api.get("/auth/me").then(({ data }) => setMe(data)).catch(() => {});
  }, []);

  const pickClient = (cid) => {
    const c = clients.find((x) => x.id === cid);
    if (!c) { setForm((f) => ({ ...f, client_id: "", client_name: "", contact_email: "", contact_name: "" })); return; }
    const mainCt = (c.contacts || []).find((x) => x.is_main && (x.email || "").trim());
    const anyCt = (c.contacts || []).find((x) => (x.email || "").trim());
    const chosen = mainCt || anyCt || {};
    setForm((f) => ({ ...f, client_id: cid, client_name: c.name, contact_email: (chosen.email || c.email || "").trim(), contact_name: chosen.name || "" }));
  };
  const selClient = clients.find((x) => x.id === form.client_id);
  const contactOptions = (selClient?.contacts || []).filter((x) => (x.email || "").trim());

  const save = async () => {
    if (!form.title.trim() || !form.date) { toast.error("Give the call a title and a date"); return; }
    setSaving(true);
    try {
      await api.post("/calls", { ...form, duration: Number(form.duration) || 30 });
      toast.success("Call scheduled — it's on your calendar");
      setShowNew(false);
      setForm(emptyForm);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  const send = async () => {
    if (!sendTo.trim()) { toast.error("Add an email address"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/calls/${sendFor.id}/send`, { to_email: sendTo.trim() });
      toast.success(data.message);
      setSendFor(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    setSending(false);
  };

  const del = async (c) => {
    if (!window.confirm(`Delete the call "${c.title}"? Its calendar entry goes too.`)) return;
    try {
      await api.delete(`/calls/${c.id}`);
      toast.success("Call deleted");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const join = async (c) => {
    try {
      const { data } = await api.get(`/calls/${c.id}`);
      setJoining(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (joining) {
    return (
      <div className="min-h-screen bg-[#F4F7FA]" data-testid="call-host-page">
        <AdminHeader title={`Hosting: ${joining.title}`} testid="call-host-title" eyebrow="WEMS Portal" backTo="/admin/calls" backTestid="call-host-back" />
        <main className="max-w-6xl mx-auto p-4 sm:p-6">
          <div className="grid lg:grid-cols-[1fr_280px] gap-4">
            <WebRTCCall token={joining.token} name={`${me?.name || "WEMS"} (Host)`} hostKey={joining.host_key} isHost onEnded={() => setJoining(null)} />
            <CallFiles token={joining.token} by="WEMS" testid="call-files" />
          </div>
          <p className="text-[11px] text-slate-400 mt-3">Guests see a waiting room until you let them in — knock alerts appear above the video. No logins, no third-party apps: it's all ours.</p>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F7FA]" data-testid="calls-page">
      <AdminHeader title="Video calls" testid="calls-title" backTo="/admin" backTestid="calls-back" eyebrow="WEMS Portal" />
      <main className="max-w-3xl mx-auto p-4 sm:p-6 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-slate-500">Schedule a video call, send the client a link, and it lands on your calendar. Guests join from phone or laptop — you let them in.</p>
          <button onClick={() => setShowNew((v) => !v)} data-testid="call-new"
            className="h-11 px-4 rounded-lg bg-[#005EB8] hover:bg-[#004a93] text-white text-sm font-bold inline-flex items-center gap-2 transition-colors shrink-0">
            <Plus className="h-4 w-4" /> New call
          </button>
        </div>

        {showNew && (
          <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3" data-testid="call-form">
            <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="What's the call about? e.g. Festival planning chat" className={inputCls} data-testid="call-title-input" />
            <select value={form.client_id} onChange={(e) => pickClient(e.target.value)} className={inputCls} data-testid="call-client">
              <option value="">Link a client (optional)…</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {!form.client_id && (
              <input value={form.client_name} onChange={(e) => setForm((f) => ({ ...f, client_name: e.target.value }))} placeholder="Who's it with?" className={inputCls} data-testid="call-client-name" />
            )}
            {contactOptions.length > 0 && (
              <select value={form.contact_email} onChange={(e) => {
                  const ct = contactOptions.find((x) => x.email === e.target.value);
                  setForm((f) => ({ ...f, contact_email: e.target.value, contact_name: ct ? (ct.name || "") : "" }));
                }} className={inputCls} data-testid="call-contact">
                <option value="">Pick the exact person to invite…</option>
                {contactOptions.map((ct, i) => <option key={i} value={ct.email}>{ct.name || ct.email}{ct.is_main ? " (main contact)" : ""} — {ct.email}</option>)}
              </select>
            )}
            <select value={form.event_id} onChange={(e) => setForm((f) => ({ ...f, event_id: e.target.value }))} className={inputCls} data-testid="call-event">
              <option value="">Which event is the call about? (optional)…</option>
              {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.name}{ev.event_date ? ` — ${ev.event_date}` : ""}</option>)}
            </select>
            <div className="grid grid-cols-3 gap-2">
              <input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className={inputCls} data-testid="call-date" />
              <input type="time" value={form.time} onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))} className={inputCls} data-testid="call-time" />
              <input type="number" min="5" step="5" value={form.duration} onChange={(e) => setForm((f) => ({ ...f, duration: e.target.value }))} placeholder="Mins" title="Approx. minutes" className={inputCls} data-testid="call-duration" />
            </div>
            <div className="flex gap-2">
              <button onClick={save} disabled={saving} data-testid="call-save"
                className="flex-1 h-11 rounded-lg bg-[#005EB8] hover:bg-[#004a93] disabled:opacity-50 text-white text-sm font-bold inline-flex items-center justify-center gap-2 transition-colors">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Video className="h-4 w-4" />} Schedule call
              </button>
              <button onClick={() => setShowNew(false)} className="h-11 px-4 rounded-lg border border-slate-300 text-sm font-bold text-slate-600" data-testid="call-cancel">Cancel</button>
            </div>
          </div>
        )}

        {list === null && <p className="text-sm text-slate-400">Loading…</p>}
        {list?.length === 0 && <p className="text-sm text-slate-400 bg-white rounded-2xl border border-slate-200 p-6 text-center" data-testid="calls-empty">No calls scheduled yet.</p>}
        {list?.map((c) => (
          <div key={c.id} className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`call-card-${c.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <p className="font-heading font-bold text-[#0F2547]">{c.title}</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {[c.date, c.time].filter(Boolean).join(" · ")}{c.duration ? ` · ~${c.duration} min` : ""}{c.client_name ? ` · ${c.client_name}` : ""}{c.event_name ? ` · re: ${c.event_name}` : ""}
                </p>
                {c.sent_to && <p className="text-xs text-slate-400 mt-0.5">Invite sent to {c.contact_name ? `${c.contact_name} — ` : ""}{c.sent_to}</p>}
                {(c.email_opens > 0 || c.email_clicks > 0) && (
                  <p className="text-xs font-semibold text-[#005EB8] mt-0.5" data-testid={`call-tracking-${c.id}`}>
                    {c.email_opens > 0 ? `Opened ${c.email_opens}×` : "Not opened yet"}{c.email_clicks > 0 ? ` · link clicked ${c.email_clicks}×` : ""}
                  </p>
                )}
              </div>
              <span className="text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wide bg-sky-50 text-[#005EB8]">
                <PhoneCall className="h-3 w-3 inline -mt-0.5 mr-1" />Call
              </span>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button onClick={() => join(c)} data-testid={`call-join-${c.id}`}
                className="h-9 px-3 rounded-lg bg-[#00A499] hover:bg-[#008f85] text-white text-xs font-bold inline-flex items-center gap-1.5 transition-colors">
                <Video className="h-3.5 w-3.5" /> Join as host
              </button>
              <button onClick={() => { setSendFor(c); setSendTo(c.contact_email || ""); }} data-testid={`call-send-${c.id}`}
                className="h-9 px-3 rounded-lg bg-[#005EB8] hover:bg-[#004a93] text-white text-xs font-bold inline-flex items-center gap-1.5 transition-colors">
                <Send className="h-3.5 w-3.5" /> Send invite
              </button>
              <button onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/call/${c.token}`); toast.success("Call link copied"); }} data-testid={`call-copy-${c.id}`}
                className="h-9 px-3 rounded-lg border border-slate-300 text-slate-600 hover:border-[#005EB8] text-xs font-bold inline-flex items-center gap-1.5 transition-colors">
                <Copy className="h-3.5 w-3.5" /> Copy link
              </button>
              <button onClick={() => del(c)} data-testid={`call-delete-${c.id}`}
                className="h-9 px-3 rounded-lg border border-red-200 text-[#D5281B] hover:bg-red-50 text-xs font-bold inline-flex items-center gap-1.5 transition-colors">
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>
            </div>
          </div>
        ))}
      </main>

      {sendFor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setSendFor(null)}>
          <div className="bg-white rounded-2xl shadow-xl p-5 w-full max-w-sm" onClick={(e) => e.stopPropagation()} data-testid="call-send-dialog">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-bold text-[#0F2547]">Send call invite</h3>
              <button onClick={() => setSendFor(null)} aria-label="Close" className="h-9 w-9 grid place-items-center rounded-lg hover:bg-slate-100"><X className="h-4 w-4" /></button>
            </div>
            <p className="text-xs text-slate-500 mb-2">{sendFor.title} · {[sendFor.date, sendFor.time].filter(Boolean).join(" · ")}</p>
            <input type="email" value={sendTo} onChange={(e) => setSendTo(e.target.value)} placeholder="client@example.com" className={inputCls} data-testid="call-send-input" />
            <button onClick={send} disabled={sending} data-testid="call-send-confirm"
              className="mt-3 w-full h-11 rounded-lg bg-[#00A499] hover:bg-[#008f85] disabled:opacity-50 text-white text-sm font-bold inline-flex items-center justify-center gap-2 transition-colors">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send invite
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
